import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "./components/ui/sonner";
import { UpsellModal } from "./components/UpsellModal";
import { CommandPalette } from "./components/CommandPalette";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { LangProvider } from "./contexts/LangContext";
import { CountryProvider } from "./contexts/CountryContext";
import { SpaceProvider } from "./contexts/SpaceContext";
import { queryClient } from "./lib/queryClient";
import { useRealtimeSync } from "./hooks/useRealtimeSync";
import Landing from "./pages/auth/Landing";
import LandingArchetypePage from "./pages/auth/LandingArchetypePage";
import DemoActivatePage from "./pages/auth/DemoActivatePage";
import ConvertDemo from "./pages/auth/ConvertDemo";
import Login from "./pages/auth/Login";
import Register from "./pages/auth/Register";
import ForgotPassword from "./pages/auth/ForgotPassword";
import ResetPassword from "./pages/auth/ResetPassword";
import VerifyEmail from "./pages/auth/VerifyEmail";
import VerifyEmailPending from "./pages/auth/VerifyEmailPending";
import EmailPreferences from "./pages/EmailPreferences";
import Preferences from "./pages/Preferences";
import Help from "./pages/Help";
import MyReferrals from "./pages/MyReferrals";
import Onboarding from "./pages/onboarding/Onboarding";
import Dashboard from "./pages/dashboard/Dashboard";
import GlobalDashboard from "./pages/dashboard/GlobalDashboard";
import History from "./pages/dashboard/History";
import Report from "./pages/dashboard/Report";
import QuickAdd from "./pages/transactions/QuickAdd";
import Receivables from "./pages/transactions/Receivables";
import Payments from "./pages/transactions/Payments";
import Investments from "./pages/wealth/Investments";
import Debts from "./pages/wealth/Debts";
import Goals from "./pages/wealth/Goals";
import Scenarios from "./pages/scenarios/Scenarios";
import Workspace from "./pages/workspace/Workspace";
import SharedOperations from "./pages/SharedOperations";
import ReadOnlyView from "./pages/readonly/ReadOnlyView";
import AcceptInvite from "./pages/spaces/AcceptInvite";
import Integrations from "./pages/integrations/Integrations";
import Recommendations from "./pages/profile/Recommendations";
import Profile from "./pages/profile/Profile";
import CashAccounts from "./pages/profile/CashAccounts";
import BotGuide from "./pages/integrations/BotGuide";
import Calendar from "./pages/transactions/Calendar";
import AdminPanel from "./pages/admin/AdminPanel";
import Pricing from "./pages/billing/Pricing";
import MyPlan from "./pages/billing/MyPlan";
import Privacy from "./pages/legal/Privacy";
import Terms from "./pages/legal/Terms";
import PackCheckout from "./pages/billing/PackCheckout";
import PaymentReturn from "./pages/billing/PaymentReturn";
import "./App.css";

function Protected({ children, requireOnboarded = true }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (requireOnboarded && !user.onboarded) return <Navigate to="/onboarding" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading...</div>;
  if (user) return <Navigate to={user.onboarded ? "/dashboard" : "/onboarding"} replace />;
  return children;
}

function AppRoutes() {
  const { user } = useAuth();
  // Open a WS only after the user is authenticated. The hook itself reads the
  // token from localStorage so we don't have to pass it explicitly.
  useRealtimeSync(!!user && user !== false);
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/for/:slug" element={<LandingArchetypePage />} />
      <Route path="/demo/activate/:token" element={<DemoActivatePage />} />
      <Route path="/convert" element={<ConvertDemo />} />
      {/* Legacy ES slug — redirect any /para/* link to /for/* (dynamic
          slug-aware: ES slug names also no longer exist, so users land
          on / if the slug isn't recognised). */}
      <Route path="/para/:slug" element={<Navigate to="/" replace />} />
      <Route path="/privacidad" element={<Privacy />} />
      <Route path="/privacy" element={<Navigate to="/privacidad" replace />} />
      <Route path="/terminos" element={<Terms />} />
      <Route path="/terms" element={<Navigate to="/terminos" replace />} />
      <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
      <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
      <Route path="/forgot-password" element={<PublicOnly><ForgotPassword /></PublicOnly>} />
      <Route path="/reset-password" element={<PublicOnly><ResetPassword /></PublicOnly>} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/verify-email-pending" element={<VerifyEmailPending />} />
      <Route path="/email-preferences" element={<EmailPreferences />} />
      <Route path="/onboarding" element={<Protected requireOnboarded={false}><Onboarding /></Protected>} />
      <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
      <Route path="/dashboard/global" element={<Protected><GlobalDashboard /></Protected>} />
      <Route path="/add" element={<Protected><QuickAdd /></Protected>} />
      <Route path="/receivables" element={<Protected><Receivables /></Protected>} />
      <Route path="/payments" element={<Protected><Payments /></Protected>} />
      <Route path="/report" element={<Protected><Report /></Protected>} />
      {/* Plural alias: keeps marketing/email links to /reports working. */}
      <Route path="/reports" element={<Navigate to="/report" replace />} />
      <Route path="/history" element={<Protected><History /></Protected>} />
      <Route path="/investments" element={<Protected><Investments /></Protected>} />
      <Route path="/debts" element={<Protected><Debts /></Protected>} />
      <Route path="/goals" element={<Protected><Goals /></Protected>} />
      <Route path="/scenarios" element={<Protected><Scenarios /></Protected>} />
      <Route path="/workspace" element={<Protected><Workspace /></Protected>} />
      <Route path="/shared" element={<Protected><SharedOperations /></Protected>} />
      {/* iter-170ai — Public read-only accountant link. No auth, no
          sidebar — the token in the URL IS the credential. */}
      <Route path="/r/:token" element={<ReadOnlyView />} />
      {/* iter-170al — Public accept-invite landing page. Auth-aware:
          delegates to /login?next=... when the user isn't signed in. */}
      <Route path="/invite/:token" element={<AcceptInvite />} />
      <Route path="/integrations" element={<Protected><Integrations /></Protected>} />
      <Route path="/bots-guide" element={<BotGuide />} />
      <Route path="/recommendations" element={<Protected><Recommendations /></Protected>} />
      <Route path="/profile" element={<Protected><Profile /></Protected>} />
      <Route path="/preferences" element={<Protected><Preferences /></Protected>} />
      <Route path="/help" element={<Protected><Help /></Protected>} />
      <Route path="/me/referrals" element={<Protected><MyReferrals /></Protected>} />
      {/* Primary route is /accounts (renamed from "Cuentas de efectivo" → "Cuentas"
          per iter-109 UX overhaul). /cash-accounts kept as alias for any
          deployed bookmark / external link / Telegram bot deep-link. */}
      <Route path="/accounts" element={<Protected><CashAccounts /></Protected>} />
      <Route path="/cash-accounts" element={<Navigate to="/accounts" replace />} />
      <Route path="/calendar" element={<Protected><Calendar /></Protected>} />
      <Route path="/admin" element={<Protected><AdminPanel /></Protected>} />
      <Route path="/pricing" element={<Protected><Pricing /></Protected>} />
      <Route path="/my-plan" element={<Protected><MyPlan /></Protected>} />
      <Route path="/billing/pack-checkout/:packId" element={<Protected><PackCheckout /></Protected>} />
      <Route path="/payments/return" element={<Protected><PaymentReturn /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <LangProvider>
          <CountryProvider>
            <SpaceProvider>
              <BrowserRouter>
                <AppRoutes />
                <UpsellModal />
                <CommandPalette />
                <Toaster position="top-right" richColors />
              </BrowserRouter>
            </SpaceProvider>
          </CountryProvider>
        </LangProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}
